/**
 * Crypto Utilities - Encryption and decryption helpers
 */

import nacl from 'tweetnacl';
import util from 'tweetnacl-util';

export class CryptoUtils {
  /**
   * Generate a new Ed25519 key pair
   */
  static generateKeyPair() {
    return nacl.sign.keyPair();
  }

  /**
   * Generate a new X25519 key pair for encryption
   */
  static generateBoxKeyPair() {
    return nacl.box.keyPair();
  }

  /**
   * Sign a message with Ed25519
   */
  static sign(message, secretKey) {
    const messageBytes = new TextEncoder().encode(message);
    const signature = nacl.sign.detached(messageBytes, secretKey);
    return util.encodeBase64(signature);
  }

  /**
   * Verify a signature
   */
  static verify(message, signature, publicKey) {
    const messageBytes = new TextEncoder().encode(message);
    const signatureBytes = util.decodeBase64(signature);
    return nacl.sign.detached.verify(messageBytes, signatureBytes, publicKey);
  }

  /**
   * Encrypt a message with X25519 + XSalsa20 + Poly1305
   */
  static encrypt(message, recipientPublicKey, senderSecretKey) {
    const nonce = nacl.randomBytes(nacl.box.nonceLength);
    const messageBytes = new TextEncoder().encode(message);

    const encrypted = nacl.box(
      messageBytes,
      nonce,
      recipientPublicKey,
      senderSecretKey
    );

    return {
      ciphertext: util.encodeBase64(encrypted),
      nonce: util.encodeBase64(nonce),
    };
  }

  /**
   * Decrypt a message
   */
  static decrypt(encryptedMessage, senderPublicKey, recipientSecretKey) {
    const ciphertext = util.decodeBase64(encryptedMessage.ciphertext);
    const nonce = util.decodeBase64(encryptedMessage.nonce);

    const decrypted = nacl.box.open(
      ciphertext,
      nonce,
      senderPublicKey,
      recipientSecretKey
    );

    if (!decrypted) {
      throw new Error('Failed to decrypt message');
    }

    return new TextDecoder().decode(decrypted);
  }

  /**
   * Hash data with SHA-256
   */
  static async hash(data) {
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Generate a random ID
   */
  static generateId() {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Convert Ed25519 public key to X25519
   */
  static ed25519ToX25519(ed25519PublicKey) {
    // This is a simplified version - in production use proper conversion
    return ed25519PublicKey.slice(0, 32);
  }

  /**
   * Derive a shared secret using X25519
   */
  static deriveSharedSecret(privateKey, publicKey) {
    return nacl.scalarMult(privateKey, publicKey);
  }

  /**
   * Encrypt for storage
   */
  static encryptForStorage(data, password) {
    // Derive key from password (simplified - use proper KDF in production)
    const key = nacl.hash(new TextEncoder().encode(password)).slice(0, 32);
    const nonce = nacl.randomBytes(nacl.secretbox.nonceLength);

    const dataBytes = new TextEncoder().encode(JSON.stringify(data));
    const encrypted = nacl.secretbox(dataBytes, nonce, key);

    return {
      ciphertext: util.encodeBase64(encrypted),
      nonce: util.encodeBase64(nonce),
    };
  }

  /**
   * Decrypt from storage
   */
  static decryptFromStorage(encryptedData, password) {
    const key = nacl.hash(new TextEncoder().encode(password)).slice(0, 32);
    const ciphertext = util.decodeBase64(encryptedData.ciphertext);
    const nonce = util.decodeBase64(encryptedData.nonce);

    const decrypted = nacl.secretbox.open(ciphertext, nonce, key);

    if (!decrypted) {
      throw new Error('Failed to decrypt data');
    }

    return JSON.parse(new TextDecoder().decode(decrypted));
  }
}

export default CryptoUtils;
